import { useSnackbar } from "notistack";
import type { RefCallback } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { getCallRecordingFile } from "./callRecordingsApi";

interface UseCallRecordingPlayback {
  readonly audioRef: RefCallback<HTMLAudioElement>;
  readonly playingRecId: string | null;
  readonly loadingRecId: string | null;
  readonly handlePlayToggle: (recId: string) => Promise<void>;
}

export function useCallRecordingPlayback(): UseCallRecordingPlayback {
  const { enqueueSnackbar } = useSnackbar();

  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const objectUrlRef = useRef<string | null>(null);
  const [playingRecId, setPlayingRecId] = useState<string | null>(null);
  const [loadingRecId, setLoadingRecId] = useState<string | null>(null);

  const stopPlayback = useCallback(() => {
    audioElementRef.current?.pause();
    setPlayingRecId(null);
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, []);

  // callback ref: attach/detach the "ended" listener exactly when the <audio> DOM node
  // mounts/unmounts, independent of when the caller renders the element relative to this
  // hook's effects (a plain ref + effect would miss a node that mounts conditionally/later)
  const audioRef = useCallback<RefCallback<HTMLAudioElement>>(
    (element) => {
      if (!element) {
        audioElementRef.current = null;
        return undefined;
      }
      audioElementRef.current = element;
      element.addEventListener("ended", stopPlayback);
      return () => {
        element.removeEventListener("ended", stopPlayback);
        audioElementRef.current = null;
      };
    },
    [stopPlayback],
  );

  // stop playback and release the blob URL if the accordion collapses (unmounts) while playing
  useEffect(() => stopPlayback, [stopPlayback]);

  const handlePlayToggle = async (recId: string) => {
    if (playingRecId === recId) {
      stopPlayback();
      return;
    }
    stopPlayback();
    try {
      setLoadingRecId(recId);
      const blob = await getCallRecordingFile(recId);
      const url = URL.createObjectURL(blob);
      objectUrlRef.current = url;
      if (audioElementRef.current) {
        audioElementRef.current.src = url;
        await audioElementRef.current.play();
        setPlayingRecId(recId);
      }
    } catch {
      enqueueSnackbar("Nie udało się odtworzyć nagrania rozmowy", { variant: "error" });
    } finally {
      setLoadingRecId(null);
    }
  };

  return { audioRef, playingRecId, loadingRecId, handlePlayToggle };
}
